import { debounce } from '@mui/material';
import { Activity } from 'api/activities/activities.types';
import { TaxRate } from 'api/taxRates/taxRates.types';
import { Unit } from 'api/units/units.types';
import {
  CorrectiveInvoice,
  CurrencyCode,
  Invoice,
  InvoiceLine,
} from 'pages/invoices/invoices.types';
import { Noop } from 'react-hook-form';
import {
  recalculateCorrectedTotals,
  recalculatePreviousTotals,
  recalculateTotals,
} from 'utils/invoiceFunctions.utils';
import { toFixedCurrency } from 'utils/numberFunctions.utils';
import { FormInputs } from './types';

export const createHandleChange = (
  setOptions: React.Dispatch<React.SetStateAction<any>>,
  setParams: React.Dispatch<React.SetStateAction<any>>,
) =>
  debounce((value: string) => {
    setOptions([]);
    setParams((prev: object) => ({
      ...prev,
      filterText: value,
      pageNumber: 1,
    }));
  }, 800);

export const createHandleOnBlur =
  (
    setOptions: React.Dispatch<React.SetStateAction<any>>,
    setParams: React.Dispatch<React.SetStateAction<any>>,
    onBlur: Noop,
  ) =>
  () => {
    onBlur();
    setParams((prev: object) => ({
      ...prev,
      filterText: '',
      pageNumber: 1,
    }));
    setOptions([]);
  };

const getRounded2Float = (val: number) => Number(toFixedCurrency(val, 2));

export const calculateNetValue = (
  quantity: number,
  price: number,
  exchangeRate: number,
) => {
  const priceWithExchangeRate = Number(
    toFixedCurrency(price * exchangeRate, 6),
  );
  return getRounded2Float(quantity * priceWithExchangeRate);
};

export const calculateTaxValue = (netValue: number, taxRate: TaxRate) => {
  const value = netValue * (taxRate ? taxRate.value / 100 : 0);
  return getRounded2Float(value);
};

export const calculateGrossValue = (netValue: number, taxValue: number) => {
  const value = getRounded2Float(netValue) + getRounded2Float(taxValue);
  return getRounded2Float(value);
};

export const prepareInvoiceLineData = ({
  activity,
  quantity,
  exchangeRate,
  description,
  grossValue,
  netValue,
  taxRate,
  taxValue,
  currencyCode,
  value,
  unit,
  vehicleMileage,
}: FormInputs): InvoiceLine => ({
  activity: activity as Activity,
  unit: unit as Unit,
  quantity: Number(quantity),
  ordinalNumber: 0,
  jobItem: null,
  exchangeRate: exchangeRate as number,
  description,
  amounts: {
    grossValue: grossValue as number,
    netValue: netValue as number,
    netValueAllocationSum: 0,
    taxRate: taxRate as TaxRate,
    taxValue: taxValue as number,
  },
  currencyAmount: {
    currencyCode: currencyCode as CurrencyCode,
    markup: value as number,
    netValue: 0,
    value: Number(value),
  },
  netUnitPriceInInvoiceCurrency: Number(
    toFixedCurrency(Number(value) * (exchangeRate as number), 6),
  ),
  vehicleMileage,
});

export const prepareAddInvoiceLinePayload = (
  invoice: Invoice | CorrectiveInvoice,
  newInvoiceLine: InvoiceLine,
) => {
  const { createdFrom, ...decodedInvoiceData } = invoice;

  const recalculatedInvoice = recalculateTotals({
    ...decodedInvoiceData,
    invoiceLines: [...invoice.invoiceLines, newInvoiceLine],
  });

  const invoiceUpdated = {
    ...recalculatedInvoice,
    __type: 'Invoice',
    buyerCounterparty: {
      id: recalculatedInvoice.buyerCounterparty.id,
    },
    currencyCode: {
      id: recalculatedInvoice.currencyCode.id,
    },
    sellerCounterparty: {
      id: recalculatedInvoice.sellerCounterparty.id,
    },
    owner: recalculatedInvoice.owner
      ? { id: recalculatedInvoice.owner.id }
      : undefined,
  };
  return invoiceUpdated;
};

export const prepareAddCorrectiveInvoiceLinePayload = (
  invoice: CorrectiveInvoice,
  newInvoiceLine: InvoiceLine,
  isPrevious: boolean,
) => {
  const { createdFrom, ...invoiceData } = invoice;

  const recalculatedInvoice = isPrevious
    ? recalculatePreviousTotals({
        ...invoiceData,
        previousInvoiceLines: [
          ...invoiceData.previousInvoiceLines,
          newInvoiceLine,
        ],
      })
    : recalculateCorrectedTotals({
        ...invoiceData,
        correctedInvoiceLines: [
          ...invoiceData.correctedInvoiceLines,
          newInvoiceLine,
        ],
      });

  const invoiceUpdated = {
    ...recalculatedInvoice,
    __type: 'CorrectiveInvoice',
    buyerCounterparty: {
      id: recalculatedInvoice.buyerCounterparty.id,
    },
    currencyCode: {
      id: recalculatedInvoice.currencyCode.id,
    },
    sellerCounterparty: {
      id: recalculatedInvoice.sellerCounterparty.id,
    },
    owner: recalculatedInvoice.owner
      ? { id: recalculatedInvoice.owner.id }
      : undefined,
  };
  return invoiceUpdated;
};

export const prepareUpdateInvoiceLinePayload = (
  invoice: Invoice | CorrectiveInvoice,
  invoiceLineId: number,
  updatedInvoiceLine: InvoiceLine,
) => {
  const updatedLines = invoice.invoiceLines.map((line) => {
    if (line.id === invoiceLineId) {
      return {
        ...line,
        ...updatedInvoiceLine,
        jobItem: line.jobItem,
        ordinalNumber: line.ordinalNumber,
      };
    }
    return line;
  });

  const { createdFrom, ...invoiceData } = invoice;

  const recalculatedInvoice = recalculateTotals({
    ...invoiceData,
    invoiceLines: updatedLines,
  });

  const invoiceUpdated = {
    ...recalculatedInvoice,
    __type: 'Invoice',
    buyerCounterparty: {
      id: recalculatedInvoice.buyerCounterparty.id,
    },
    currencyCode: {
      id: recalculatedInvoice.currencyCode.id,
    },
    sellerCounterparty: {
      id: recalculatedInvoice.sellerCounterparty.id,
    },
    owner: recalculatedInvoice.owner
      ? { id: recalculatedInvoice.owner.id }
      : undefined,
  };
  return invoiceUpdated;
};

export const prepareUpdateCorrectiveInvoiceLinePayload = (
  invoice: CorrectiveInvoice,
  invoiceLineId: number,
  updatedInvoiceLine: InvoiceLine,
  isPrevious: boolean,
) => {
  const updatedLines = (
    isPrevious ? invoice.previousInvoiceLines : invoice.correctedInvoiceLines
  ).map((line) => {
    if (line.id === invoiceLineId) {
      return {
        ...line,
        ...updatedInvoiceLine,
        jobItem: line.jobItem,
        ordinalNumber: line.ordinalNumber,
      };
    }
    return line;
  });

  const { createdFrom, ...invoiceData } = invoice;

  const recalculatedInvoice = isPrevious
    ? recalculatePreviousTotals({
        ...invoiceData,
        previousInvoiceLines: updatedLines,
      })
    : recalculateCorrectedTotals({
        ...invoiceData,
        correctedInvoiceLines: updatedLines,
      });

  const invoiceUpdated = {
    ...recalculatedInvoice,
    __type: 'CorrectiveInvoice',
    buyerCounterparty: {
      id: recalculatedInvoice.buyerCounterparty.id,
    },
    currencyCode: {
      id: recalculatedInvoice.currencyCode.id,
    },
    sellerCounterparty: {
      id: recalculatedInvoice.sellerCounterparty.id,
    },
    owner: recalculatedInvoice.owner
      ? { id: recalculatedInvoice.owner.id }
      : undefined,
  };
  return invoiceUpdated;
};
